import type { Column, ColumnDef, Row } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/table-core";
import type { CSSProperties } from "react";

import {
  ButtonCell,
  type ButtonCellProps,
  defaultCell,
  JobTitleCell,
  TextCell,
  type TextCellValue,
} from "@/components/table/cells";
import { CreatedAtCell } from "@/components/table/cells/created-at-cell";
import { LocationCell } from "@/components/table/cells/location-cell";
import { getCommonPinningStyles } from "@/components/table/utils";
import { INFO, UpwageBadge } from "@/components/upwage-badge";
import {
  BaseTableQueryKeys,
  SortOrder,
  type ApiColumn,
  type BaseTableQueryParams,
} from "@/types/api";
import {
  ScreenerLocationStatus,
  type ScreenerLocationStatusTypeWithUnpublished,
} from "@/types/screener-location";
import type { Screener, ScreenerOrArchivedScreener } from "@/types/screeners";
import { cellWithInfoProp } from "@/utils/table";

export interface ScreenersTableQueryParams extends BaseTableQueryParams {
  jobTitle: string[];
  location: string[];
  status: ScreenerLocationStatusTypeWithUnpublished[];
  userId: string[];
}

export const ScreenersQueryKeys = {
  ...BaseTableQueryKeys,
  jobTitle: "jobTitle",
  location: "location",
  status: "status",
  userId: "userId",
} as const satisfies { [K in keyof ScreenersTableQueryParams]: K };

export const ScreenersFilterKeys = [
  "jobTitle",
  "location",
  "search",
  "status",
  "userId",
] as const satisfies readonly (keyof ScreenersTableQueryParams)[];

export interface ArchivedScreenersTableQueryParams
  extends ScreenersTableQueryParams {
  archivedAt: Date;
  getArchived: true;
}

export enum ScreenersTableSortableColumns {
  CreatedAt = "createdAt",
  InternalName = "internalName",
  JobTitle = "jobTitle",
  LastSubmissionDate = "lastSubmissionDate",
  Screenings = "screenings",
  Status = "status",
  Version = "version",
  Wage = "wage",
}

export const ARCHIVED_AT = "archivedAt" as const;

export const SCREENERS_DEFAULT_SORT = {
  sortBy: ScreenersTableSortableColumns.LastSubmissionDate,
  sortOrder: SortOrder.Desc,
} as const;

export const getScreenerPinningStyles = (
  column: Column<Screener>,
  row?: Row<Screener>,
): CSSProperties => ({
  ...getCommonPinningStyles(column),
  backgroundColor:
    !row ||
    row.original.status !== ScreenerLocationStatus.Paused ||
    row.original.isDraft
      ? "white"
      : "#F6F6F6",
});

export const formatScreenerColumns = (
  columns: ApiColumn<ScreenerOrArchivedScreener>[],
  isArchived: boolean,
  buttonCellProps: Omit<ButtonCellProps<ScreenerOrArchivedScreener>, "info">,
  // Because the column definitions array is consumed by Tanstack Table rather
  // than directly by us, I'm not particularly worried about `any` here. The
  // actual return type would be something like
  // ColumnDef<ScreenerOrArchivedScreener, string | Competency | unknown | ...
  // and even once working with that return type TS takes issue with unknown
  // further down the chain.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<ScreenerOrArchivedScreener, any>[] => {
  const columnHelper = createColumnHelper<ScreenerOrArchivedScreener>();

  return (
    columns
      // See comment in function definition for explanation
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map<ColumnDef<ScreenerOrArchivedScreener, any>>((column) => {
        const { fieldName, internalName: header } = column;

        switch (fieldName) {
          case "jobTitle":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, string>(
                isArchived ? TextCell : JobTitleCell,
                {},
              ),
              header,
            });
          case "internalName":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, TextCellValue>(
                TextCell,
                {},
              ),
              header,
            });
          case "screenings":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, TextCellValue>(
                TextCell,
                {},
              ),
              header,
            });
          case "status":
            return columnHelper.accessor(fieldName, {
              cell: (info) => {
                if (
                  "isDraft" in info.row.original &&
                  info.row.original.isDraft
                ) {
                  return "Not published";
                }

                return (
                  <UpwageBadge
                    className="w-[72px]"
                    colorVariant={info.getValue()}
                    filled
                  >
                    {info.getValue()}
                  </UpwageBadge>
                );
              },
              header,
            });
          case "location":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, string[]>(
                LocationCell,
                {},
              ),
              header,
            });
          case "lastSubmissionDate":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, string>(
                CreatedAtCell,
                {},
              ),
              header,
            });
          case "createdAt":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, string>(
                CreatedAtCell,
                {},
              ),
              header,
            });
          case "archivedAt":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, string>(
                CreatedAtCell,
                {},
              ),
              header,
            });
          case "wage":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, TextCellValue>(
                TextCell,
                {},
              ),
              header,
            });
          case "type":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, TextCellValue>(
                TextCell,
                {},
              ),
              header,
            });
          case "userId":
            return columnHelper.accessor(fieldName, {
              cell: cellWithInfoProp<ScreenerOrArchivedScreener, TextCellValue>(
                TextCell,
                {},
              ),
              header,
            });
          case "version":
            return columnHelper.accessor(fieldName, {
              cell: (info) => (
                <UpwageBadge
                  className="min-w-[42px]"
                  colorVariant={INFO}
                  filled
                >
                  {info.getValue()}
                </UpwageBadge>
              ),
              header,
            });
          default:
            return columnHelper.accessor(fieldName, {
              ...defaultCell<ScreenerOrArchivedScreener, string | number>(
                column,
              ),
            });
        }
      })
      .concat([
        columnHelper.display({
          header: "Actions",
          id: "actions",
          cell: cellWithInfoProp<
            ScreenerOrArchivedScreener,
            void,
            ButtonCellProps<ScreenerOrArchivedScreener>
          >(ButtonCell, buttonCellProps),
        }),
      ])
  );
};
